import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import FigoLottie from './FigoLottie';
import loadingWheel from '../../assets/lottieFiles/loading-blue.json';
import FigoLoadingOverlay from './FigoLoadingOverlay';
import { OneIncDefaultPaymentMethod } from './OneIncDefaultPaymentMethod';
import {
  createOneIncSession,
  oneIncLoadDefaultPayments,
  oneIncSessionRemoved,
} from '../../actions/oneInc';
import {
  BANK_ACCOUNT,
  CREDIT_CARD,
  PAYMENT_METHOD_NAMES,
  PAYMENT_METHOD_IDS,
  ONE_INC_PAYMENT_CATEGORY,
} from '../../constants';

import './OneIncModal.css';
import {
  openMultiPoliciesModal,
  paymentMethodPastDueModalVisibleChanged,
} from '../../actions';
import {
  PETCLOUD_ORIGIN,
  useGetRecaptchaToken,
} from '../../hooks/useRecaptchaValidator';

let closeDefaultCount = 0;
const OneIncModal = ({
  buttonClass,
  children,
  hasPastPaymentDue,
  onUpdatePaymentMethod,
  selectedPolicy,
}) => {
  const dispatch = useDispatch();
  const getRecaptchaToken = useGetRecaptchaToken();
  const store = useSelector(({
    billingPayments,
    editPayment,
    oneInc,
    personalInformation,
    policies,
  }) => ({
    billingPayments,
    editPayment,
    oneInc,
    personalInformation,
    policies,
  }), shallowEqual);
  const div = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [closeDefaultModal, setCloseDefaultModal] = useState(0);
  const {
    billingPayments: { billingDetails },
    editPayment: { policiesList },
  } = store;
  const {
    firstName,
    lastName,
    userInfo: { Address, DiamondClientId, LoginEmail },
  } = store.personalInformation;

  useEffect(() => {
    if (!store.editPayment.showLoading && showLoadingModal) {
      setShowLoadingModal(false);
    }
  }, [store.editPayment.showLoading]);

  const userInformation = {
    address1: store.personalInformation.address1,
    address2: store.personalInformation.address2,
    city: store.personalInformation.city,
    state: store.personalInformation.state,
    zipCode: store.personalInformation.zipCode,
  };

  function getForceToUpdatePolicies() {
    if (!billingDetails) {
      return [];
    }

    return store.editPayment.policiesList
      .filter((policy) => (
        policy.Policy.PaymentMethod
        === PAYMENT_METHOD_NAMES[billingDetails.PayMethod]
      ));
  }

  function handleUpdatePaymentMethod(oneIncPaymentData = {}) {
    onUpdatePaymentMethod({
      paymentMethodData: oneIncPaymentData,
      policies: [],
      policyNumber: selectedPolicy,
      sameBillingAddress: true,
      userInformation,
    });
  }

  const paymentMethodShared = useCallback((selectedPaymentMethod) => {
    if (!selectedPaymentMethod) {
      return false;
    }

    const otherPoliciesWithSamePayMethod = policiesList
      .filter((policy) => policy && policy.Policy
        .PaymentMethod === selectedPaymentMethod
        && policy.Policy.Number !== selectedPolicy);

    // if same payment method for other policies
    return otherPoliciesWithSamePayMethod.length > 0;
  }, [policiesList, selectedPolicy]);

  function showMultiplePolicyModal(oneIncPaymentData = {}) {
    const policies = getForceToUpdatePolicies()
      .map((policy) => (policy.Policy.Number));

    dispatch(openMultiPoliciesModal({
      paymentMethodData: oneIncPaymentData,
      policies,
      sameBillingAddress: true,
      userInformation,
    }));
  }

  const handleSaveComplete = useCallback(({ data, saveAsDefault = false }) => {
    const isCreditCard =
      data.paymentCategory === ONE_INC_PAYMENT_CATEGORY.creditCard;
    const paymentName = isCreditCard ? CREDIT_CARD : BANK_ACCOUNT;

    const oneIncPaymentData = {
      ...data,
      paymentName,
      policyNumber: selectedPolicy,
    };
    const hasOnlyOnePolicy = policiesList.length === 1;

    // reacently added payment method
    // or changed from already existing list (not new)
    if (hasOnlyOnePolicy || saveAsDefault) {
      if (hasPastPaymentDue) {
        dispatch(paymentMethodPastDueModalVisibleChanged({
          requestUpdatePaymentMethod: {
            paymentMethodData: oneIncPaymentData,
            policies: [],
            policyNumber: selectedPolicy,
            sameBillingAddress: true,
            userInformation,
          },
          visible: true,
        }));
      } else {
        setShowLoadingModal(true);
        handleUpdatePaymentMethod(oneIncPaymentData);
      }
    } else if (paymentMethodShared(paymentName)) {
      // if payment method is shared with other policies
      showMultiplePolicyModal(oneIncPaymentData);
    } else {
      // if payment method is not shared with other policies
      setShowLoadingModal(true);
      handleUpdatePaymentMethod(oneIncPaymentData);
    }

    setLoading(false);
  }, [hasPastPaymentDue, selectedPolicy, policiesList]);

  const handleSaveByDefault = useCallback(({
    customerName,
    lastFourDigits,
    paymentCategory,
    tokenId,
  }) => {
    const data = {
      customerName,
      lastFourDigits,
      paymentCategory,
      sessionId: store.oneInc.portalOneSessionKey,
      tokenId,
    };

    handleSaveComplete({ data, saveAsDefault: true });
  }, [handleSaveComplete]);

  const callback = useCallback(() => {
    closeDefaultCount += 1;
    setCloseDefaultModal(closeDefaultCount + 1);
  }, []);

  useEffect(() => {
    if (!div || !window.$) {
      return;
    }

    window.$('#portalOneContainer').on('portalOne.load', callback);

    window.$('#portalOneContainer').on('portalOne.unload', () => {
      setLoading(false);
      dispatch(oneIncSessionRemoved());
    });

    window.$('#portalOneContainer').on('portalOne.saveComplete', (_, data) => {
      // add new payment method scenario
      handleSaveComplete({ data });
    });

    window.$('#portalOneContainer').on('portalOne.error', () => {
      setLoading(false);
      dispatch(oneIncSessionRemoved());
    });
  }, [dispatch, div]);

  useEffect(() => {
    if (!store.oneInc.portalOneSessionKey || !div) {
      return;
    }

    const billingAddressStreet = Address.City && Address.State
      ? `${Address.AddressLine1} ${Address.AddressLine2}`
      + ` ${Address.City}, ${Address.State}`
      : '';

    const params = {
      billingAddressStreet,
      billingZip: Address.ZipCode,
      clientReferenceData1: DiamondClientId,
      confirmationDisplay: 'false',
      paymentCategory: 'UserSelect',
      policyHolderName: `${firstName} ${lastName}`,
      sessionId: store.oneInc.portalOneSessionKey,
    };

    window.$('#portalOneContainer').portalOne();
    window.$('#portalOneContainer').data('portalOne').savePaymentMethod(params);
  }, [
    Address,
    DiamondClientId,
    firstName,
    store.oneInc.portalOneSessionKey,
    lastName,
  ]);

  const onAddPaymentMethod = async () => {
    setLoading(true);

    const token = await getRecaptchaToken();

    if (token) {
      dispatch(oneIncLoadDefaultPayments({
        email: LoginEmail,
        origin: PETCLOUD_ORIGIN,
        token,
      }));
    }
  };

  const onCreateSession = async () => {
    const token = await getRecaptchaToken();

    if (token) {
      dispatch(createOneIncSession({
        email: LoginEmail,
        origin: PETCLOUD_ORIGIN,
        token,
      }));
    }
  };

  function onCancelDefaultMondal() {
    setLoading(false);
  }

  function renderUpdateButton() {
    if (loading) {
      return (
        <div className={buttonClass}>
          <FigoLottie
            animationData={loadingWheel}
            height={30}
            width={30}
          />
        </div>
      );
    }

    return (
      <button
        className={buttonClass}
        onClick={onAddPaymentMethod}
        type="button"
      >
        <div className="No-click">
          {children}
        </div>
      </button>
    );
  }

  return (
    <>
      <FigoLoadingOverlay visible={showLoadingModal} />

      {/* Modal with payment methods already created */}
      <OneIncDefaultPaymentMethod
        forceClose={closeDefaultModal}
        isCreditCardDefault={billingDetails && billingDetails.PayMethod
          === PAYMENT_METHOD_IDS.creditCard}
        onCancel={onCancelDefaultMondal}
        oneIncReducer={store.oneInc}
        onNewAddPaymentMethod={onCreateSession}
        onSaveComplete={handleSaveByDefault}
      />

      {renderUpdateButton()}

      {/* OneInc modal create here */}
      {/* Modal for add new payment method (BA or CC) */}
      <div id="portalOneContainer" />
    </>
  );
};

export { OneIncModal };
